import { useCallback, useMemo } from 'react';
import { CheckboxFilterSection, CheckboxFilterItem } from '../DynamicTable';

export interface UseTableFilteringProps {
  enableFiltering: boolean;
  checkboxFilters: CheckboxFilterSection[];
  onFiltersChange?: (filters: CheckboxFilterSection[]) => void;
  onFiltersReset?: () => void;
}

export interface UseTableFilteringReturn {
  activeFiltersCount: number;
  onFilterChange: (
    sectionId: CheckboxFilterSection['id'],
    item: CheckboxFilterItem,
  ) => void;
  onResetFilters: () => void;
}

export const useTableFiltering = ({
  enableFiltering,
  checkboxFilters,
  onFiltersChange,
  onFiltersReset,
}: UseTableFilteringProps): UseTableFilteringReturn => {
  const activeFiltersCount = useMemo(() => {
    if (!enableFiltering || !checkboxFilters) {
      return 0;
    }

    return checkboxFilters.reduce((count, section) => {
      return count + section.items.filter((item) => item.value).length;
    }, 0);
  }, [enableFiltering, checkboxFilters]);

  const onFilterChange = useCallback(
    (sectionId: CheckboxFilterSection['id'], item: CheckboxFilterItem) => {
      if (!onFiltersChange) {
        return;
      }

      const updatedFilters = checkboxFilters.map((section) => {
        if (section.id === sectionId) {
          const updatedItems = section.items.map((filterItem) => {
            if (filterItem.id === item.id) {
              return { ...filterItem, value: item.value };
            }

            return filterItem;
          });

          return { ...section, items: updatedItems };
        }

        return section;
      });

      onFiltersChange(updatedFilters);
    },
    [checkboxFilters, onFiltersChange],
  );

  const onResetFilters = useCallback(() => {
    if (onFiltersReset) {
      onFiltersReset();
    }
  }, [onFiltersReset]);

  return {
    activeFiltersCount,
    onFilterChange,
    onResetFilters,
  };
};
